import React from "react";
import {
    Box,
    Typography,
    Grid,
    Button,
    IconButton,
    FormControl,
    FormControlLabel,
    RadioGroup,
    Radio,
    Divider,
    FormHelperText,
    CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { LuxurysalesMastery } from "../../../../api/services";
import BoxInput from "../../../../Components/BoxInput";
import { toast } from 'react-toastify';


interface Props {
    onClose: () => void;
}

interface FormValues {
    name: string;
    email: string;
    phone: string;
    role: string;
    organization: string;
    market: string;
    dealSize: string;
    largestDeal: string;
    clients: string;
    ceiling: string;
    startTime: string;
    mode: string;
}

const schema: yup.ObjectSchema<FormValues> = yup.object({
    name: yup.string().required("Name is required"),
    email: yup.string().email("Invalid email").required("Email is required"),
    phone: yup.string().required("Phone is required"),
    role: yup.string().required("Role is required"),
    organization: yup.string().required("Organization is required"),
    market: yup.string().required("Market is required"),
    dealSize: yup.string().required("Deal size is required"),
    largestDeal: yup.string().required("Largest deal is required"),
    clients: yup.string().required("Client type is required"),
    ceiling: yup.string().required("Sales ceiling is required"),
    startTime: yup.string().required("Start time is required"),
    mode: yup.string().required("Preferred mode is required"),
});

const LuxurySalesMasteryForm: React.FC<Props> = ({ onClose }) => {
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: yupResolver(schema),
        defaultValues: {
            name: "",
            email: "",
            phone: "",
            role: "",
            organization: "",
            market: "",
            dealSize: "",
            largestDeal: "",
            clients: "",
            ceiling: "",
            startTime: "",
            mode: "",
        },
    });

    const mutation = useMutation({
        mutationFn: LuxurysalesMastery,
        onSuccess: () => {
            toast.success('Successfully submitted!');
            reset();
            onClose();
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message?.[0] || "Submission failed");
        },
    });

    const onSubmit = (data: FormValues) => {
        mutation.mutate({
            name: data.name,
            email: data.email,
            phoneNumber: data.phone,
            currentRole: data.role,
            organization: data.organization,
            primaryMarket: data.market,
            salesCeiling: data.ceiling,
            largestDealClosed: data.largestDeal,
            clientType: data.clients as any,
            averageDealSize: data.dealSize as any,
            preferredMode: data.mode as any,
            readyToBegin: data.startTime as any,
        });
    };

    const luxuryField = {
        "& .MuiOutlinedInput-root": {
            borderRadius: "16px",
            backgroundColor: "#ffffff",
            "& fieldset": {
                borderColor: "rgba(15,99,165,0.15)",
            },
            "&:hover fieldset": {
                borderColor: "#0f63a5",
            },
            "&.Mui-focused fieldset": {
                borderColor: "#0f63a5",
                boxShadow: "0 0 0 4px rgba(15,99,165,0.08)",
            },// ✅ USER TYPED TEXT COLOR
    "& input": {
      color: "#000",
    },

    "& textarea": {
      color: "#000",
    },

    // ✅ PLACEHOLDER COLOR
    "& input::placeholder": {
      color: "#9e9e9e",
      opacity: 1,
    },

    "& textarea::placeholder": {
      color: "#9e9e9e",
      opacity: 1,
    },

    // "& fieldset": {
    //   borderColor: "rgba(15,99,165,0.15)",
    // },
    // "&:hover fieldset": {
    //   borderColor: primary,
    // },
    // "&.Mui-focused fieldset": {
    //   borderColor: primary,
    //   boxShadow: "0 0 0 4px rgba(15,99,165,0.08)",
    // },
        },
    };

    return (
        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: { xs: 3, md: 6 }, borderRadius: "28px", background: "#ffffff", boxShadow: "0 40px 120px rgba(0,0,0,0.15)", position: "relative" }}>
            <IconButton onClick={onClose} sx={{ position: "absolute", top: 20, right: 20 }}>
                <CloseIcon />
            </IconButton>

            <Typography variant="h4" fontWeight={700} mb={5}>
                Luxury Sales Mastery
            </Typography>

            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <Controller name="name" control={control} render={({ field }) => (
                        <BoxInput {...field} placeholder="Name*" sx={{  ...luxuryField }} error={!!errors.name} helperText={errors.name?.message} />
                    )} />

                    <Controller name="phone" control={control} render={({ field }) => (
                        <BoxInput {...field} placeholder="Phone Number*" sx={{ mt: 2, ...luxuryField }} error={!!errors.phone} helperText={errors.phone?.message} />
                    )} />

                    <Controller name="organization" control={control} render={({ field }) => (
                        <BoxInput {...field} placeholder="Organization / Brand*" sx={{ mt: 2, ...luxuryField }} error={!!errors.organization} helperText={errors.organization?.message} />
                    )} />

                    <Controller name="market" control={control} render={({ field }) => (
                        <BoxInput {...field} placeholder="Primary Market You Operate In*" multiline rows={1.4} sx={{ mt: 2, ...luxuryField }} error={!!errors.market} helperText={errors.market?.message} />
                    )} />

                    <Typography variant="h6" sx={{ mt: 3, fontSize: { xs: "14px", sm: "16px" } }}>Who Are Your Typical Clients?*</Typography>
                    <Controller
                        name="clients"
                        control={control}
                        render={({ field }) => (
                            <FormControl error={!!errors.clients}>
                                <RadioGroup {...field}>
                                    <FormControlLabel value="NRIs" control={<Radio />} label={
                                        <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                            NRIs
                                        </Typography>
                                    } />
                                    <FormControlLabel value="Ultra-HNIs" control={<Radio />} label={
                                        <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                            Ultra-HNIs
                                        </Typography>
                                    } />
                                    <FormControlLabel value="HNIs" control={<Radio />} label={
                                        <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                            HNIs
                                        </Typography>
                                    } />
                                    <FormControlLabel value="Corporate Buyers" control={<Radio />} label={
                                        <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                            Corporate Buyers
                                        </Typography>
                                    } />
                                    <FormControlLabel value="Institutional" control={<Radio />} label={
                                        <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                            Institutional
                                        </Typography>
                                    } />
                                </RadioGroup>
                                <FormHelperText>{errors.clients?.message}</FormHelperText>
                            </FormControl>
                        )}
                    />

                    <Typography variant="h6" sx={{ mt: 3,fontSize: { xs: "14px", sm: "16px" }  }}>Preferred Mode</Typography>
                    <Controller
                        name="mode"
                        control={control}
                        render={({ field }) => (
                            <FormControl error={!!errors.mode}>
                                <RadioGroup {...field}>
                                    <FormControlLabel value="Online" control={<Radio />} label={
                                        <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                            Online
                                        </Typography>
                                    } />
                                    <FormControlLabel value="Hybrid" control={<Radio />} label={
                                        <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                            Hybrid
                                        </Typography>
                                    } />
                                    <FormControlLabel value="In-person" control={<Radio />} label={
                                        <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                            In-person
                                        </Typography>
                                    } />
                                </RadioGroup>
                                <FormHelperText>{errors.mode?.message}</FormHelperText>
                            </FormControl>
                        )}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Controller name="email" control={control} render={({ field }) => (
                        <BoxInput {...field} placeholder="Email*" error={!!errors.email} helperText={errors.email?.message} sx={{ ...luxuryField }} />
                    )} />

                    <Controller name="role" control={control} render={({ field }) => (
                        <BoxInput {...field} placeholder="Current Role / Designation*" sx={{ mt: 2, ...luxuryField }} error={!!errors.role} helperText={errors.role?.message} />
                    )} />

                    <Controller name="ceiling" control={control} render={({ field }) => (
                        <BoxInput {...field} placeholder="What ceiling are you currently hitting in sales?" multiline rows={2} sx={{ mt: 2, ...luxuryField }} error={!!errors.ceiling} helperText={errors.ceiling?.message} />
                    )} />

                    <Controller name="largestDeal" control={control} render={({ field }) => (
                        <BoxInput {...field} placeholder="Largest Deal Closed Till Date*" multiline rows={1.4} sx={{ mt: 2, ...luxuryField }} error={!!errors.largestDeal} helperText={errors.largestDeal?.message} />
                    )} />

                    <Typography variant="h6" sx={{ mt: 3 ,fontSize: { xs: "14px", sm: "16px" } }}>Current Average Deal Size*</Typography>
                    <Controller
                        name="dealSize"
                        control={control}
                        render={({ field }) => (
                            <FormControl error={!!errors.dealSize}>
                                <RadioGroup {...field}>
                                    <FormControlLabel value="25L–1Cr" control={<Radio />} label={
                                        <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                            25L-1Cr
                                        </Typography>
                                    } />
                                    <FormControlLabel value="1Cr–5Cr" control={<Radio />} label={
                                        <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                            1Cr-5Cr
                                        </Typography>
                                    } />
                                    <FormControlLabel value="₹5Cr+" control={<Radio />} label={
                                        <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                            ₹5Cr+
                                        </Typography>
                                    } />
                                </RadioGroup>
                                <FormHelperText>{errors.dealSize?.message}</FormHelperText>
                            </FormControl>
                        )}
                    />

                    <Typography variant="h6" sx={{ mt: 3, fontSize: { xs: "14px", sm: "16px" } }}>If selected, when are you ready to begin?</Typography>
                    <Controller
                        name="startTime"
                        control={control}
                        render={({ field }) => (
                            <FormControl error={!!errors.startTime}>
                                <RadioGroup {...field}>
                                    <FormControlLabel value="Immediately" control={<Radio />} label={
                                        <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                            Immediately
                                        </Typography>
                                    } />
                                    <FormControlLabel value="Within 30 Days" control={<Radio />} label={
                                        <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                            Within 30 Days
                                        </Typography>
                                    } />
                                    <FormControlLabel value="Exploring" control={<Radio />} label={
                                        <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                            Exploring
                                        </Typography>
                                    } />
                                </RadioGroup>
                                <FormHelperText>{errors.startTime?.message}</FormHelperText>
                            </FormControl>
                        )}
                    />
                </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Button fullWidth type="submit" variant="contained" disabled={mutation.isPending}>
                {mutation.isPending ? <CircularProgress size={24} /> : "Submit Application"}
            </Button>
        </Box>
    );
};

export default LuxurySalesMasteryForm;